import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Trash2, RotateCcw, AlertTriangle, ArrowLeft, ChevronDown, ChevronRight } from "lucide-react";
import { getTrashedContacts, restoreContact, bulkRestoreContacts } from "../../../api/contact";
import ConfirmModal from "../../../components/common/ConfirmModal";
import toast from "react-hot-toast";
import { hasAdminAccess } from "../../../utils/roleAccess";

// Group heading on this page is the delete action/batch that removed the
// contact (e.g. "Thornbury Vendor List") rather than `source` — a single
// per-row delete has no batch, so those fall under this shared heading.
const INDIVIDUAL_GROUP_KEY = "__individual__";
const INDIVIDUAL_GROUP_LABEL = "Deleted Individually";

export default function ContactTrashPage() {
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const [trashed, setTrashed] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [restoring, setRestoring] = useState(null);
  const [confirmRestore, setConfirmRestore] = useState(null);
  const [confirmBulkRestore, setConfirmBulkRestore] = useState(null);
  const [bulkRestoring, setBulkRestoring] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState(new Set());

  useEffect(() => {
    if (!hasAdminAccess(user)) {
      navigate("/contacts");
      return;
    }
    fetchTrash();
  }, []);

  const fetchTrash = async () => {
    setIsLoading(true);
    try {
      const res = await getTrashedContacts();
      setTrashed(res.data);
    } catch {
      toast.error("Failed to load trash");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRestore = async () => {
    if (!confirmRestore) return;
    setRestoring(confirmRestore.id);
    try {
      await restoreContact(confirmRestore.id);
      toast.success(`"${confirmRestore.name}" restored successfully`);
      setTrashed((prev) => prev.filter((c) => c.id !== confirmRestore.id));
    } catch {
      toast.error("Failed to restore contact");
    } finally {
      setRestoring(null);
      setConfirmRestore(null);
    }
  };

  const handleBulkRestore = async () => {
    if (!confirmBulkRestore) return;
    const { label, contacts } = confirmBulkRestore;
    const ids = contacts.map((c) => c.id);
    setBulkRestoring(true);
    try {
      await bulkRestoreContacts(ids);
      toast.success(`Restored ${ids.length} contact${ids.length === 1 ? "" : "s"} from "${label}"`);
      setTrashed((prev) => prev.filter((c) => !ids.includes(c.id)));
    } catch {
      toast.error("Failed to restore group");
    } finally {
      setBulkRestoring(false);
      setConfirmBulkRestore(null);
    }
  };

  const toggleGroup = (key) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const groups = useMemo(() => {
    const byBatch = {};
    for (const contact of trashed) {
      const key = contact.deletion_batch_id || INDIVIDUAL_GROUP_KEY;
      if (!byBatch[key]) {
        byBatch[key] = {
          key,
          label: contact.deletion_batch_label || INDIVIDUAL_GROUP_LABEL,
          sortAt: contact.deletion_batch_deleted_at || contact.deleted_at,
          contacts: [],
        };
      }
      byBatch[key].contacts.push(contact);
    }
    return Object.values(byBatch).sort((a, b) => new Date(b.sortAt) - new Date(a.sortAt));
  }, [trashed]);

  const urgencyColor = (days) => {
    if (days <= 7) return "text-red-600 bg-red-50 border-red-200";
    if (days <= 30) return "text-orange-600 bg-orange-50 border-orange-200";
    return "text-gray-500 bg-gray-50 border-gray-200";
  };

  return (
    <div className="flex-1 flex flex-col bg-[#F8FAFC] min-h-screen -mx-[14px] -my-3 sm:-mx-4 sm:-my-4 md:-m-6">
      <div className="bg-white border-b border-gray-100 sticky top-0 z-40 shadow-sm">
        <div className="w-full px-4 sm:px-6 md:px-8 py-4 sm:py-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate("/contacts")}
              className="w-10 h-10 flex-shrink-0 bg-white border border-gray-200 rounded-xl flex items-center justify-center hover:bg-gray-50 transition-colors shadow-sm"
            >
              <ArrowLeft className="w-5 h-5 text-[#374151]" />
            </button>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-50 rounded-xl">
                <Trash2 className="w-5 h-5 text-red-500" />
              </div>
              <div>
                <h1 className="text-xl font-black text-[#374151] tracking-tighter">Contact Trash</h1>
                <p className="text-xs text-gray-400 font-medium">Contacts are permanently deleted after 180 days</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full px-4 sm:px-6 md:px-8 py-6">
        {isLoading ? (
          <div className="flex items-center justify-center py-24">
            <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-[#55C5D0]" />
          </div>
        ) : trashed.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <Trash2 className="w-8 h-8 text-gray-300" />
            </div>
            <h3 className="text-lg font-bold text-gray-400">Trash is empty</h3>
            <p className="text-sm text-gray-300 mt-1">Deleted contacts will appear here</p>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-1">
              {trashed.length} {trashed.length === 1 ? "contact" : "contacts"} in trash
            </p>
            {groups.map(({ key, label, contacts }) => {
              const isCollapsed = collapsedGroups.has(key);
              return (
                <div key={key} className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                  <div className="flex items-center justify-between gap-4 px-5 py-3.5 bg-gray-50/60 border-b border-gray-100">
                    <button
                      onClick={() => toggleGroup(key)}
                      className="flex items-center gap-2 min-w-0 flex-1 text-left"
                    >
                      {isCollapsed ? (
                        <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      )}
                      <span className="text-sm font-bold text-[#374151] truncate">
                        {label}
                      </span>
                      <span className="text-xs font-semibold text-gray-400 flex-shrink-0">
                        {contacts.length} {contacts.length === 1 ? "contact" : "contacts"}
                      </span>
                    </button>
                    <button
                      onClick={() => setConfirmBulkRestore({ label, contacts })}
                      className="flex items-center gap-2 px-3 py-1.5 bg-white border border-[#55C5D0] text-[#2E8E97] hover:bg-[#55C5D0]/10 rounded-lg text-xs font-bold transition-all active:scale-95 flex-shrink-0"
                    >
                      <RotateCcw className="w-3.5 h-3.5" />
                      Restore all
                    </button>
                  </div>

                  {!isCollapsed && (
                    <div className="divide-y divide-gray-100">
                      {contacts.map((contact) => (
                        <div
                          key={contact.id}
                          className="p-5 flex items-center justify-between gap-4"
                        >
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-bold text-[#374151] truncate">{contact.name}</p>
                            <p className="text-xs text-gray-400 mt-0.5 truncate">
                              {contact.email || contact.phone || "No email or phone on file"}
                            </p>
                            <p className="text-[11px] text-gray-400 mt-1">
                              Deleted {new Date(contact.deleted_at).toLocaleDateString("en-AU")}
                            </p>
                            <p className="text-[11px] text-gray-400 mt-0.5">
                              Archived by: <span className="font-medium text-gray-500">{contact.archived_by || "Unknown"}</span>
                            </p>
                          </div>

                          <div className="flex items-center gap-3 flex-shrink-0">
                            <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold border ${urgencyColor(contact.days_until_permanent_deletion)}`}>
                              {contact.days_until_permanent_deletion <= 7 && <AlertTriangle className="w-3 h-3" />}
                              {contact.days_until_permanent_deletion}d left
                            </span>
                            <button
                              onClick={() => setConfirmRestore(contact)}
                              disabled={restoring === contact.id}
                              className="flex items-center gap-2 px-4 py-2 bg-[#55C5D0] hover:bg-[#4AADB7] text-white rounded-xl text-xs font-bold transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <RotateCcw className="w-3.5 h-3.5" />
                              Restore
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <ConfirmModal
        isOpen={!!confirmRestore}
        onClose={() => setConfirmRestore(null)}
        onConfirm={handleRestore}
        loading={restoring !== null}
        title="Restore Contact"
        description={`Restore "${confirmRestore?.name}" back to your active Contacts?`}
        confirmText="Restore"
        variant="primary"
      />

      <ConfirmModal
        isOpen={!!confirmBulkRestore}
        onClose={() => setConfirmBulkRestore(null)}
        onConfirm={handleBulkRestore}
        loading={bulkRestoring}
        title="Restore Group"
        description={`Restore all ${confirmBulkRestore?.contacts.length} contact${confirmBulkRestore?.contacts.length === 1 ? "" : "s"} in "${confirmBulkRestore?.label}" back to your active Contacts?`}
        confirmText="Restore all"
        variant="primary"
      />
    </div>
  );
}
